import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { INotification, ISitePermit } from '~/client/graph'
import {
  GetSitePermitAggregationListDocument,
  IGetSitePermitAggregationListQueryVariables,
} from '~/client/graph/operations/generated/SitePermits.generated'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import PermitBallInCourtStore from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import {
  isDeliveryType,
  isPermitType,
  isWorkflowMessageType,
} from '~/client/src/shared/types/NotificationTypes'
import Guard from '~/client/src/shared/utils/Guard'

import BaseNotification from '../../models/Notification'
import Workflow from '../../models/Workflow'
import {
  LOAD_NOTIFICATIONS,
  MARK_NOTIFICATION_AS_READ,
} from '../EventStore/eventConstants'
import EventsStore from '../EventStore/Events.store'
import ProjectDateStore from '../ui/ProjectDate.store'
import DeliveriesStore from './Deliveries.store'
import DeliveryStatusChangesStore from './DeliveryStatusChanges.store'
import GraphExecutorStore from './GraphExecutor.store'
import WorkflowTypesStore from './WorkflowTypes.store'

const SORT_KEY = 'createdAt'

export default class NotificationsStore {
  public notifications = observable(new Map<string, BaseNotification>())
  @observable public displayedNotification: BaseNotification = null
  @observable public savedScrollTop: number = 0
  private state: InitialState
  public permitBicStore: PermitBallInCourtStore = null

  public constructor(
    private eventsStore: EventsStore,
    private deliveriesStore: DeliveriesStore,
    private deliveryStatusChangesStore: DeliveryStatusChangesStore,
    private readonly companiesStore: CompaniesStore,
    private readonly graphExecutorStore: GraphExecutorStore,
    private readonly projectDateStore: ProjectDateStore,
    private readonly workflowTypesStore: WorkflowTypesStore,
  ) {
    Guard.requireAll({
      eventsStore,
      deliveriesStore,
      deliveryStatusChangesStore,
      graphExecutorStore,
    })
    this.state = eventsStore.appState

    makeObservable(this)
  }

  @action.bound
  public receiveList(notificationDtos: INotification[]) {
    const notificationsMap = notificationDtos.reduce((map, n) => {
      map[n.id] = BaseNotification.fromDto(n)
      return map
    }, {})
    this.notifications.merge(notificationsMap)
  }

  @action.bound
  public receiveOne(
    id: string,
    dto: INotification | null,
  ): BaseNotification | null {
    if (dto) {
      const model = BaseNotification.fromDto(dto)
      const existing = this.notifications.get(id)
      if (existing?.associatedPermit) {
        model.associatedPermit = existing.associatedPermit
      }
      this.notifications.set(id, model)
      return model
    } else {
      this.notifications.delete(id)
      return null
    }
  }

  @action.bound
  public markDisplayedAsRead() {
    if (this.displayedNotification && !this.displayedNotification.wasRead) {
      this.eventsStore.dispatch(
        MARK_NOTIFICATION_AS_READ,
        this.displayedNotification,
      )
    }
  }

  @action.bound
  public resetUIState() {
    this.markDisplayedAsRead()
    this.displayedNotification = null
  }

  @action.bound
  public clearNotifications() {
    this.notifications.clear()
    this.unreadNotificationsCount = 0
    this.resetUIState()
  }

  public get isLoading() {
    return this.state.loading.get(LOAD_NOTIFICATIONS)
  }

  @computed
  public get all(): BaseNotification[] {
    return Array.from(this.notifications.values())
  }

  public getLatestByEntityId(entityId?: string): BaseNotification {
    if (!entityId) {
      return null
    }
    return (
      this.latestForEachEntityFromActiveProject.find(
        n => n.entityId === entityId,
      ) || null
    )
  }

  @computed
  public get latestForEachEntityFromActiveProject(): BaseNotification[] {
    // Single pass: keep only the latest notification per entityId
    const latestByEntity = new Map<string, BaseNotification>()
    for (const n of this.notifications.values()) {
      const existing = latestByEntity.get(n.entityId)
      if (!existing || n.createdAt > existing.createdAt) {
        latestByEntity.set(n.entityId, n)
      }
    }

    // Single filter + sort
    const result: BaseNotification[] = []
    for (const n of latestByEntity.values()) {
      if (isPermitType(n.type) || isWorkflowMessageType(n.type)) {
        if (!n.associatedPermit) continue
      } else if (isDeliveryType(n.type)) {
        if (!this.deliveriesStore?.byId?.get(n.entityId)) continue
      }
      result.push(n)
    }

    return result.sort(descending(SORT_KEY))
  }

  @observable public unreadNotificationsCount = 0

  @action.bound
  public setUnreadNotificationsCount(count: number) {
    this.unreadNotificationsCount = count
  }

  public getUniquePermitEntityIds(notifications: INotification[]): string[] {
    return Array.from(
      notifications.reduce((uniqueIds, notification) => {
        if (
          (isPermitType(notification.type) ||
            isWorkflowMessageType(notification.type)) &&
          notification.entityId
        ) {
          uniqueIds.add(notification.entityId)
        }
        return uniqueIds
      }, new Set<string>()),
    )
  }

  public async fetchPermits(permitIds: string[]) {
    if (!permitIds?.length) return

    const projectId = this.state.activeProject.id

    const variables: IGetSitePermitAggregationListQueryVariables = {
      filter: {
        ids: permitIds,
        projectId,
        withDeleted: true,
      },
      limit: permitIds.length,
    }

    const permitsResult = await this.graphExecutorStore.query(
      GetSitePermitAggregationListDocument,
      variables,
    )

    if (permitsResult?.data?.sitePermitsAggregation?.data) {
      const permits = permitsResult.data.sitePermitsAggregation.data

      // O(n) — build index once
      const byEntityId = new Map<string, BaseNotification[]>()
      for (const n of this.notifications.values()) {
        if (
          (isPermitType(n.type) || isWorkflowMessageType(n.type)) &&
          n.entityId
        ) {
          const list = byEntityId.get(n.entityId)
          if (list) {
            list.push(n)
          } else {
            byEntityId.set(n.entityId, [n])
          }
        }
      }

      // O(permits) — single batch update
      runInAction(() => {
        for (const permitDto of permits) {
          const permit = Workflow.fromDto(permitDto)
          const matches = byEntityId.get(permitDto.id)
          if (matches) {
            for (const n of matches) {
              n.associatedPermit = permit
            }
          }
        }
      })
    }
  }

  private setPermitForNotifications(permitDto: ISitePermit) {
    const permit = Workflow.fromDto(permitDto)
    runInAction(() => {
      for (const n of this.notifications.values()) {
        if (
          (isPermitType(n.type) || isWorkflowMessageType(n.type)) &&
          n.entityId === permitDto.id
        ) {
          n.associatedPermit = permit
        }
      }
    })
  }

  public handlePermitChange(permitDto: ISitePermit) {
    if (!permitDto) return

    this.setPermitForNotifications(permitDto)
  }
}

function descending(key: string) {
  return (a, b) => b[key] - a[key]
}
